/**
 * Utility functions for handling location display and formatting
 */

/**
 * Checks if a location string looks like coordinates (e.g., "33.5124, -82.2047")
 */
export const isCoordinateString = (location: string): boolean => {
  if (!location) return false;
  
  // Check if it matches coordinate pattern: number, comma, space (optional), number
  const coordinatePattern = /^-?\d+\.?\d*,\s*-?\d+\.?\d*$/;
  return coordinatePattern.test(location.trim());
};

/**
 * Formats a location for display, handling coordinate strings gracefully
 * @param location - The location string from the profile
 * @param fallbackText - Text to show if location looks like coordinates
 * @returns Formatted location string
 */
export const formatLocationForDisplay = (
  location: string | null | undefined, 
  fallbackText: string = "Location not set"
): string => {
  if (!location) return fallbackText;
  
  // If it looks like coordinates, return fallback text instead
  if (isCoordinateString(location)) {
    return fallbackText;
  }
  
  return location;
};

/**
 * Reverse geocodes coordinates to get a readable address
 * Uses the same service as the main location service but simplified for utility use
 */
export const reverseGeocodeCoordinates = async (coordinateString: string): Promise<string> => {
  try {
    const parts = coordinateString.split(',').map(p => p.trim());
    if (parts.length !== 2) return coordinateString;
    
    const lat = parseFloat(parts[0]);
    const lng = parseFloat(parts[1]);
    
    if (isNaN(lat) || isNaN(lng)) return coordinateString;
    
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`
    );
    
    if (!response.ok) return coordinateString;
    
    const data = await response.json();
    const address = data.address || {};
    
    // Try to build a nice city, state format
    const city = address.city || address.town || address.village || address.municipality || '';
    const state = address.state || address.province || '';
    const country = address.country || '';
    
    if (city && state) {
      return `${city}, ${state}`;
    } else if (city && country) {
      return `${city}, ${country}`;
    } else if (data.display_name) {
      // Fallback to display name but try to shorten it
      const parts = data.display_name.split(',');
      if (parts.length >= 2) {
        return `${parts[0].trim()}, ${parts[1].trim()}`;
      }
      return data.display_name;
    }
    
    return coordinateString; // Fallback to original if we can't parse
  } catch (error) {
    console.warn('Error reverse geocoding coordinates:', error);
    return coordinateString;
  }
};